import { Popover } from "@mui/material";
import {
  Code as CodeIcon,
  Hash as HashIcon,
  List as ListIcon,
} from "lucide-react";
import * as React from "react";

import { RoboqlQuery } from "@/shared/domain/query";

import type { FilterDefinition } from "./FilterDefinition";
import styles from "./FilterMenu.module.css";
import type {
  ActiveFilters,
  FilterMenuMode,
  FilterMenuOutput,
  RoboqlMenuConfig,
} from "./FilterModel";
import { MetricsFilterMenu } from "./MetricsFilterMenu";
import { PropertyFilterMenu } from "./PropertyFilterMenu";
import { RoboqlFilterMenu } from "./RoboqlFilterMenu";

export interface FilterMenuProps {
  /** Whether the menu is currently open. */
  open: boolean;
  /** Element the popover anchors to (the trigger button). */
  anchorEl: HTMLElement | null;
  /**
   * Called when the user clicks outside the menu, presses Escape, or
   * commits a filter (in which case `onCommit` fires first). The caller
   * is responsible for transitioning their popup state to closed.
   */
  onClose: () => void;
  /** Filterable fields the menu offers in its Properties pane. */
  filterDefinitions: FilterDefinition[];
  /**
   * Fired when the user commits a single configured filter — structured
   * filter (from the Properties pane) or roboql query (from the RoboQL
   * pane). `onClose` fires immediately after.
   */
  onCommit: (output: FilterMenuOutput) => void;
  /**
   * Current committed filter state. Used to seed the RoboQL editor on
   * open (`filters.query`) and to gate the conflict banner. Optional;
   * the menu treats absence as `{ type: "none" }`.
   */
  filters?: ActiveFilters;
  /**
   * RoboQL-mode configuration. When provided, the menu surfaces the
   * mode bar (Properties | or | RoboQL) and the RoboQL editor pane.
   * When absent, the menu renders only `<PropertyFilterMenu>` (today's
   * UX) — existing consumers without a roboql dependency stay
   * unchanged.
   */
  roboql?: RoboqlMenuConfig;
  /**
   * When true, the menu surfaces a Metrics tab (a searchable tree pane that
   * fetches the org's metric definitions itself).
   */
  metrics?: boolean;
  /**
   * Which pane to show on open. Defaults to `"properties"`. The
   * chip-edit popover on the RoboQL chip will set this to `"roboql"` so
   * the editor opens pre-filled.
   */
  initialMode?: FilterMenuMode;
  /**
   * Field the Properties pane opens with already chosen. Set by callers that
   * open the menu on the customer's behalf — the View picker's empty-state
   * chips name a field, so landing them on the field picker would throw away
   * the choice they just made.
   */
  initialField?: FilterDefinition | null;
}

/**
 * Anchored popover containing the filter menu. Shared between
 * `<ResourceView.Filters>` (the labeled outlined button in the Actions
 * row) and `<ResourceView.ActiveFilters>`'s inline `+` button so both
 * triggers open the same menu and route through the same commit handler.
 *
 * When the optional `roboql` prop is provided, the menu becomes a
 * two-mode shell: a mode bar at the top switches between Properties
 * (structured filter form) and RoboQL (Monaco editor) panes. When
 * omitted, the menu renders only the Properties pane and matches
 * today's UX exactly — no mode bar, no banner.
 */
export function FilterMenu({
  open,
  anchorEl,
  onClose,
  filterDefinitions,
  onCommit,
  filters,
  roboql,
  metrics = false,
  initialMode = "properties",
  initialField = null,
}: FilterMenuProps) {
  // Reset mode on every open so the chip-edit popover (initialMode =
  // "roboql") doesn't bleed into the next outer-trigger open.
  const [mode, setMode] = React.useState<FilterMenuMode>(initialMode);
  React.useEffect(() => {
    if (open) {
      setMode(initialMode);
    }
  }, [open, initialMode]);

  const showModeBar = roboql !== undefined || Boolean(metrics);

  // Conflict banner: surface only when the current pane would overwrite
  // state of the OTHER variant on commit. Suppressed when the relevant
  // variant has zero content (no surprise to warn about).
  const conflict = buildConflict(mode, filters);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          className: styles.popover,
          role: "dialog",
          "aria-label": "Filter menu",
          // Disable MUI's default elevation shadow so the CSS module's
          // layered box-shadow has sole control over the frame depth.
          elevation: 0,
        },
      }}
    >
      {showModeBar && (
        <ModeBar
          mode={mode}
          onChange={setMode}
          hasMetrics={metrics}
          hasRoboql={roboql !== undefined}
        />
      )}
      {conflict && <ConflictBanner message={conflict.message} />}
      {mode === "properties" && (
        <div
          role="tabpanel"
          id="filter-menu-panel-properties"
          aria-labelledby="filter-menu-tab-properties"
        >
          <PropertyFilterMenu
            filterDefinitions={filterDefinitions}
            initialField={initialField}
            onCommit={(filter) => {
              onCommit({ type: "structured", filter });
              onClose();
            }}
            onCancel={onClose}
          />
        </div>
      )}
      {mode === "roboql" && roboql && (
        <div
          role="tabpanel"
          id="filter-menu-panel-roboql"
          aria-labelledby="filter-menu-tab-roboql"
        >
          <RoboqlFilterMenu
            initialValue={
              filters?.type === "roboql" ? filters.query.toString() : ""
            }
            target={roboql.target}
            onExecute={(query, spec) => {
              onCommit({
                type: "roboql",
                query: RoboqlQuery.from(query),
                spec,
              });
              onClose();
            }}
            onCancel={onClose}
          />
        </div>
      )}
      {mode === "metrics" && metrics && (
        <div
          role="tabpanel"
          id="filter-menu-panel-metrics"
          aria-labelledby="filter-menu-tab-metrics"
        >
          <MetricsFilterMenu
            onCommit={(filter) => {
              onCommit({ type: "structured", filter });
              onClose();
            }}
            onCancel={onClose}
          />
        </div>
      )}
    </Popover>
  );
}

interface ModeBarProps {
  mode: FilterMenuMode;
  onChange: (next: FilterMenuMode) => void;
  hasMetrics: boolean;
  hasRoboql: boolean;
}

function ModeBar({ mode, onChange, hasMetrics, hasRoboql }: ModeBarProps) {
  // Buttons render with `role="tab"` (not `role="radio"`) because they
  // control the `tabpanel`s rendered below — tab semantics are the right
  // a11y contract for "select one to show its panel." Visually they're
  // styled as a pill radio-group: exactly one is filled with the primary
  // color, the rest are outlined. Properties is always shown; Metrics and
  // RoboQL appear only when the consumer supplies their config.
  return (
    <>
      <div className={styles.modeBar} role="tablist" aria-label="Filter mode">
        <button
          type="button"
          role="tab"
          id="filter-menu-tab-properties"
          aria-selected={mode === "properties"}
          aria-controls="filter-menu-panel-properties"
          className={styles.modeButton}
          data-active={mode === "properties"}
          onClick={() => onChange("properties")}
        >
          <ListIcon className={styles.modeIcon} aria-hidden="true" />
          Properties
        </button>
        {hasMetrics && (
          <button
            type="button"
            role="tab"
            id="filter-menu-tab-metrics"
            aria-selected={mode === "metrics"}
            aria-controls="filter-menu-panel-metrics"
            className={styles.modeButton}
            data-active={mode === "metrics"}
            onClick={() => onChange("metrics")}
          >
            <HashIcon className={styles.modeIcon} aria-hidden="true" />
            Metrics
          </button>
        )}
        {hasRoboql && (
          <button
            type="button"
            role="tab"
            id="filter-menu-tab-roboql"
            aria-selected={mode === "roboql"}
            aria-controls="filter-menu-panel-roboql"
            className={styles.modeButton}
            data-active={mode === "roboql"}
            onClick={() => onChange("roboql")}
          >
            <CodeIcon className={styles.modeIcon} aria-hidden="true" />
            RoboQL
          </button>
        )}
      </div>
      <hr className={styles.modeBarDivider} />
    </>
  );
}

interface ConflictBannerProps {
  message: string;
}

function ConflictBanner({ message }: ConflictBannerProps) {
  return (
    <div className={styles.conflictBanner} role="status">
      <span className={styles.conflictMessage}>{message}</span>
    </div>
  );
}

interface ConflictDescriptor {
  message: string;
}

/**
 * Builds the conflict banner descriptor based on the current pane and
 * the committed filter state. Returns `null` when the active variant
 * doesn't conflict with the pane being shown (either empty state, or
 * the pane matches the variant).
 *
 * The banner is informational-only: the user clears the other variant
 * by simply committing in the current pane — the reducer
 * (`view.commit`) replaces state across the variant boundary
 * automatically (structured→roboql and roboql→structured). No explicit
 * "clear" affordance is needed in the banner; backing out is just
 * closing the menu.
 */
function buildConflict(
  mode: FilterMenuMode,
  filters: ActiveFilters | undefined,
): ConflictDescriptor | null {
  if (filters === undefined || filters.type === "none") {
    return null;
  }
  // Properties and Metrics are both structured/composable; committing in
  // either while RoboQL is active replaces the RoboQL query.
  if (
    (mode === "properties" || mode === "metrics") &&
    filters.type === "roboql"
  ) {
    return {
      message:
        "Adding a filter here will replace your active RoboQL query. RoboQL can't be combined with other filters.",
    };
  }
  if (mode === "roboql" && filters.type === "structured") {
    const n = filters.filters.length;
    return {
      message: `Executing RoboQL will replace your ${n} filter${
        n === 1 ? "" : "s"
      }. RoboQL can't be combined with other filters.`,
    };
  }
  return null;
}
